import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { SITE_URL, buildBreadcrumbJsonLd } from '../utils/seo';
import { SAVED_SEARCHES_KEY, SavedSearch } from '../overview/mysearches';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import MainHeader from '../header/MainHeader';
import Footer from '../footer/Footer';
import { listingsService, favoritesService, axiosInstance } from '../api';
import {
  FilterState, defaultFilterState, defaultPagination, PaginationState,
  buildQueryParams, colors,
} from './mysearchConstants';
import makesData from '../json/makes.json';
import QuickSearchBar from './components/QuickSearchBar';
import VehicleCard from './components/VehicleCard';
import FilterSidebar from './components/FilterSidebar';
import SearchHeader from './components/SearchHeader';
import { SkeletonGrid } from './components/SkeletonCard';
import { useTranslation } from 'react-i18next';
import './mysearch.css';

interface QsbState {
  searchQuery: string;
  make: string;
  model: string;
  isImported?: boolean;
  importCountry?: string;
}

export default function Mysearch() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Filter state (initialised from URL) ──────────────────────────────────
  const [filters, setFilters] = useState<FilterState>(() => {
    const p = new URLSearchParams(window.location.search);
    const init = { ...defaultFilterState };
    const keys: (keyof FilterState)[] = [
      'searchQuery', 'location', 'priceTo', 'priceFrom',
      'bodyType', 'condition', 'make', 'model', 'fuelType',
      'yearFrom', 'yearTo', 'mileageFrom', 'mileageTo', 'sellerType',
    ];
    keys.forEach(k => { const v = p.get(k); if (v) (init as any)[k] = v; });
    if (p.get('isImported') === 'true') init.isImported = true;
    if (p.get('importCountry')) init.importCountry = p.get('importCountry') || '';
    const ia = p.get('importAvailability');
    if (ia === 'AVAILABLE_NOW' || ia === 'READY_TO_IMPORT') init.importAvailability = ia;
    return init;
  });

  // ── Quick-search bar pending state (not applied until Search clicked) ─────
  const [qsb, setQsb] = useState<QsbState>(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      searchQuery: p.get('searchQuery') || '',
      make: p.get('make') || '',
      model: p.get('model') || '',
      isImported: p.get('isImported') === 'true',
      importCountry: p.get('importCountry') || '',
    };
  });

  // ── Listings & pagination ─────────────────────────────────────────────────
  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalOffers, setTotalOffers] = useState(0);
  const [pagination, setPagination] = useState<PaginationState>(defaultPagination);

  // ── Favorites ─────────────────────────────────────────────────────────────
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [togglingFavorite, setTogglingFavorite] = useState<string | null>(null);

  // ── Make / Model options ──────────────────────────────────────────────────
  const [makeOptions, setMakeOptions] = useState<{ value: string; label: string; image?: string }[]>([]);

  // Sidebar model options (based on filters.make)
  const [modelOptions, setModelOptions] = useState<{ value: string; label: string }[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);

  // QSB model options (based on qsb.make — independent of sidebar)
  const [qsbModelOptions, setQsbModelOptions] = useState<{ value: string; label: string }[]>([]);
  const [qsbLoadingModels, setQsbLoadingModels] = useState(false);

  // ── Colour ────────────────────────────────────────────────────────────────
  const [selectedColor, setSelectedColor] = useState('');
  const [isMetallic, setIsMetallic] = useState(false);

  // ── Mobile sidebar ────────────────────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ── Load makes ───────────────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get('/api/vehicles/makes')
      .then(res => {
        const makes: string[] = Array.isArray(res.data) ? res.data : [];
        setMakeOptions(makes.map(m => {
          const logoEntry = (makesData as any[]).find(
            (e: any) => e.makeName?.toLowerCase() === m?.toLowerCase()
          );
          return {
            value: m,
            label: m,
            image: logoEntry?.image?.thumb || logoEntry?.image?.optimized || '',
          };
        }));
      })
      .catch(() => setMakeOptions([]));
  }, []);

  // ── Load sidebar model options when filters.make changes ─────────────────
  useEffect(() => {
    if (!filters.make) { setModelOptions([]); return; }
    setLoadingModels(true);
    axiosInstance.get('/api/vehicles/models', { params: { make: filters.make } })
      .then(res => {
        const models: string[] = Array.isArray(res.data) ? res.data : [];
        setModelOptions(models.map(m => ({ value: m, label: m })));
      })
      .catch(() => setModelOptions([]))
      .finally(() => setLoadingModels(false));
  }, [filters.make]);

  // ── Load QSB model options when qsb.make changes ─────────────────────────
  useEffect(() => {
    if (!qsb.make) { setQsbModelOptions([]); return; }
    setQsbLoadingModels(true);
    axiosInstance.get('/api/vehicles/models', { params: { make: qsb.make } })
      .then(res => {
        const models: string[] = Array.isArray(res.data) ? res.data : [];
        setQsbModelOptions(models.map(m => ({ value: m, label: m })));
      })
      .catch(() => setQsbModelOptions([]))
      .finally(() => setQsbLoadingModels(false));
  }, [qsb.make]);

  // ── Load favorite IDs ────────────────────────────────────────────────────
  useEffect(() => {
    favoritesService.getFavoriteIds()
      .then(ids => setFavoriteIds(new Set(ids)))
      .catch(() => {});
  }, []);

  // ── Sync URL params → filters ─────────────────────────────────────────────
  useEffect(() => {
    const keys: (keyof FilterState)[] = [
      'bodyType', 'condition', 'make', 'model', 'priceTo', 'priceFrom',
      'mileageTo', 'yearFrom', 'location', 'fuelType', 'searchQuery',
    ];
    setFilters(prev => {
      const next = { ...prev };
      keys.forEach(k => {
        const v = searchParams.get(k);
        if (v !== null) (next as any)[k] = v;
      });
      return next;
    });
    setQsb(prev => ({
      ...prev,
      searchQuery: searchParams.get('searchQuery') ?? prev.searchQuery,
      make: searchParams.get('make') ?? prev.make,
      model: searchParams.get('model') ?? prev.model,
    }));
  }, [searchParams]);

  // ── Fetch listings ────────────────────────────────────────────────────────
  const fetchListings = useCallback(async (f = filters, pg = pagination) => {
    setLoading(true);
    try {
      const res = await listingsService.getAll(buildQueryParams(f, pg));
      if (res) {
        setListings(res.listings || []);
        setTotalOffers(res.pagination?.total ?? 0);
        setPagination(prev => ({
          ...prev,
          page: res.pagination?.page || prev.page,
          limit: res.pagination?.limit || prev.limit,
          totalPages: res.pagination?.totalPages ?? 0,
        }));
      }
    } catch {
      setListings([]);
      setTotalOffers(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchListings(filters, pagination);
  }, [
    pagination.page,
    filters.bodyType, filters.condition, filters.make, filters.model,
    filters.priceTo, filters.priceFrom, filters.mileageTo, filters.mileageFrom,
    filters.yearFrom, filters.yearTo, filters.location, filters.fuelType,
    filters.searchQuery, filters.sellerType, filters.color, filters.sortBy, filters.priceTag,
    filters.isImported, filters.importCountry, filters.importAvailability,
  ]);

  // ── Sidebar filter helpers (auto-apply) ───────────────────────────────────
  const updateFilter = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
    // Keep QSB import tab in sync with sidebar
    if (key === 'isImported') setQsb(prev => ({ ...prev, isImported: !!value }));
    if (key === 'importCountry') setQsb(prev => ({ ...prev, importCountry: value }));
  };

  const handleMakeChange = (value: string) => {
    setFilters(prev => ({ ...prev, make: value, model: '' }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleColorChange = (color: string) => {
    const next = selectedColor === color ? '' : color;
    setSelectedColor(next);
    updateFilter('color', next);
  };

  const handleMetallicChange = (val: boolean) => {
    setIsMetallic(val);
    updateFilter('isMetallic', val);
  };

  // ── QSB helpers (pending — applied only on Search click, except import fields) ──
  const updateQsbFilter = (key: keyof QsbState, value: string | boolean) => {
    setQsb(prev => ({ ...prev, [key]: value }));
    // Import fields apply immediately and sync to sidebar
    if (key === 'isImported') {
      setFilters(prev => ({ ...prev, isImported: !!value, importCountry: value ? prev.importCountry : '' }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }
    if (key === 'importCountry') {
      setFilters(prev => ({ ...prev, importCountry: String(value) }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  const handleQsbMakeChange = (value: string) => {
    setQsb(prev => ({ ...prev, make: value, model: '' }));
  };

  // ── Search button: apply QSB pending state into filters ───────────────────
  const handleSearch = () => {
    const newFilters = {
      ...filters,
      searchQuery: qsb.searchQuery,
      make: qsb.make,
      model: qsb.model,
      isImported: !!qsb.isImported,
      importCountry: qsb.isImported ? (qsb.importCountry || '') : '',
    };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const [searchSaved, setSearchSaved] = useState(false);

  const handleSaveSearch = () => {
    try {
      const raw = localStorage.getItem(SAVED_SEARCHES_KEY);
      const existing: SavedSearch[] = raw ? JSON.parse(raw) : [];
      const params: Record<string, string> = {};
      const keys: (keyof FilterState)[] = [
        'searchQuery', 'make', 'model', 'fuelType', 'bodyType',
        'yearFrom', 'yearTo', 'priceFrom', 'priceTo', 'mileageTo', 'location',
      ];
      keys.forEach(k => { if ((filters as any)[k]) params[k] = (filters as any)[k]; });
      const newSearch: SavedSearch = {
        id: Date.now().toString(),
        label: '',
        params,
        savedAt: new Date().toISOString(),
      };
      const updated = [newSearch, ...existing.slice(0, 19)];
      localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(updated));
      setSearchSaved(true);
      setTimeout(() => setSearchSaved(false), 2500);
    } catch {}
  };

  const resetFilters = () => {
    setFilters(defaultFilterState);
    setQsb({ searchQuery: '', make: '', model: '' });
    setSelectedColor('');
    setIsMetallic(false);
    setPagination(prev => ({ ...prev, page: 1 }));
    setSearchParams({});
  };

  const handleSortChange = (val: string) => updateFilter('sortBy', val);

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= (pagination.totalPages ?? 1)) {
      setPagination(prev => ({ ...prev, page: p }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ── Favorites toggle ──────────────────────────────────────────────────────
  const handleToggleFavorite = async (id: string, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (togglingFavorite === id) return;
    setTogglingFavorite(id);
    try {
      if (favoriteIds.has(id)) {
        await favoritesService.removeFavorite(id);
        setFavoriteIds(prev => { const s = new Set(prev); s.delete(id); return s; });
      } else {
        await favoritesService.addFavorite(id);
        setFavoriteIds(prev => new Set(prev).add(id));
      }
    } catch { /* favorite toggle failure is silent */ }
    finally { setTogglingFavorite(null); }
  };

  const activeFilterCount = (Object.keys(defaultFilterState) as (keyof FilterState)[])
    .filter(k => k !== 'sortBy' && k !== 'searchQuery')
    .filter(k => filters[k] !== defaultFilterState[k]).length;

  const totalPages = pagination.totalPages ?? 0;

  // ── Page numbers ──────────────────────────────────────────────────────────
  const pageNumbers = (): (number | '...')[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const cur = pagination.page;
    const pages: (number | '...')[] = [1];
    if (cur > 3) pages.push('...');
    for (let i = Math.max(2, cur - 1); i <= Math.min(totalPages - 1, cur + 1); i++) pages.push(i);
    if (cur < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  };

  const makeLabel = filters.make || qsb.make;
  const modelLabel = filters.model || qsb.model;
  const searchTitle = makeLabel
    ? modelLabel
      ? `${makeLabel} ${modelLabel} për Shitje në Shqipëri`
      : `Makina ${makeLabel} për Shitje në Shqipëri`
    : 'Makina për Shitje në Shqipëri';
  const searchDesc = makeLabel
    ? `Gjej ${makeLabel}${modelLabel ? ' ' + modelLabel : ''} të përdorura me çmime konkurruese në AutoPlace Shqipëri. ${totalOffers} oferta aktive.`
    : `Kërko mbi ${totalOffers || '10,000+'} makina të sigurta me dokumenta të plota. Filtro sipas markës, çmimit, vitit dhe vendndodhjes.`;

  const crumbs = [
    { name: 'AutoPlace', url: SITE_URL },
    { name: 'Makina', url: `${SITE_URL}/mysearch` },
    ...(makeLabel ? [{ name: makeLabel, url: `${SITE_URL}/mysearch?make=${encodeURIComponent(makeLabel)}` }] : []),
    ...(modelLabel ? [{ name: `${makeLabel} ${modelLabel}`, url: `${SITE_URL}/mysearch?make=${encodeURIComponent(makeLabel)}&model=${encodeURIComponent(modelLabel)}` }] : []),
  ];

  return (
    <div className="home-page-light ms-page">
      <SEOHead
        title={searchTitle}
        description={searchDesc}
        canonical={`${SITE_URL}/mysearch${makeLabel ? `?make=${encodeURIComponent(makeLabel)}${modelLabel ? `&model=${encodeURIComponent(modelLabel)}` : ''}` : ''}`}
        jsonLd={buildBreadcrumbJsonLd(crumbs)}
      />
      <MainHeader />

      {/* ── Quick Search Bar ── */}
      <div className="ms-search-section">
        <QuickSearchBar
          qsb={qsb}
          makeOptions={makeOptions}
          modelOptions={qsbModelOptions}
          loadingModels={qsbLoadingModels}
          onFilterChange={updateQsbFilter}
          onMakeChange={handleQsbMakeChange}
          onSearch={handleSearch}
        />
      </div>

      {/* ── Breadcrumb ── */}
      <div className="ms-breadcrumb-bar">
        <div className="ms-breadcrumb">
          <Link to="/" className="ms-breadcrumb-link">Home</Link>
          <span className="ms-breadcrumb-sep">/</span>
          <span className="ms-breadcrumb-current">Search</span>
        </div>
      </div>

      {/* ── Main Layout ── */}
      <div className="ms-layout">
        {/* Sidebar */}
        <div className={`ms-sidebar-wrapper ${sidebarOpen ? 'ms-sidebar-open' : ''}`}>
          <FilterSidebar
            filters={filters}
            makeOptions={makeOptions}
            modelOptions={modelOptions}
            loadingModels={loadingModels}
            activeFilterCount={activeFilterCount}
            selectedColor={selectedColor}
            isMetallic={isMetallic}
            onFilterChange={updateFilter}
            onMakeChange={handleMakeChange}
            onColorChange={handleColorChange}
            onMetallicChange={handleMetallicChange}
            onReset={resetFilters}
            onClose={() => setSidebarOpen(false)}
          />
        </div>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && <div className="ms-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

        {/* Results */}
        <div className="ms-results">
          {/* Mobile filter toggle */}
          <button className="ms-filter-toggle d-md-none" onClick={() => setSidebarOpen(true)}>
            Filters {activeFilterCount > 0 && <span className="ms-filter-badge">{activeFilterCount}</span>}
          </button>

          {/* Search header */}
          <SearchHeader
            total={totalOffers}
            sortBy={filters.sortBy}
            activeFilterCount={activeFilterCount}
            hasActiveSearch={!!(filters.searchQuery || filters.make || filters.model)}
            onSortChange={handleSortChange}
            onReset={resetFilters}
          />

          {/* Save search */}
          {activeFilterCount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button
                onClick={handleSaveSearch}
                style={{
                  background: searchSaved ? '#e8f5e9' : 'transparent',
                  color: searchSaved ? '#2e7d32' : 'var(--color1)',
                  border: `1.5px solid ${searchSaved ? '#a5d6a7' : 'var(--color1)'}`,
                  borderRadius: 8, padding: '6px 14px',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <i className={`bi ${searchSaved ? 'bi-bookmark-check-fill' : 'bi-bookmark-plus'}`} />
                {searchSaved ? 'Saved!' : 'Save Search'}
              </button>
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <SkeletonGrid count={6} />
          ) : listings.length === 0 ? (
            <div className="ms-empty">
              <div className="ms-empty-icon">🔍</div>
              <h3>{t('search.no_vehicles')}</h3>
              <p>{t('search.no_vehicles_desc')}</p>
              <button className="ms-empty-btn" onClick={resetFilters}>{t('search.clear_filters')}</button>
            </div>
          ) : (
            <div className="ms-grid">
              {listings.map(listing => (
                <VehicleCard
                  key={listing.id}
                  listing={listing}
                  isFavorited={favoriteIds.has(listing.id)}
                  isTogglingFavorite={togglingFavorite === listing.id}
                  onToggleFavorite={handleToggleFavorite}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="ms-pagination">
              <button
                className="ms-page-btn ms-page-arrow"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                <MdChevronLeft size={20} />
              </button>

              {pageNumbers().map((p, i) =>
                p === '...'
                  ? <span key={`dots-${i}`} className="ms-page-dots">…</span>
                  : <button
                      key={p}
                      className={`ms-page-btn ${pagination.page === p ? 'ms-page-active' : ''}`}
                      onClick={() => handlePageChange(p as number)}
                    >
                      {p}
                    </button>
              )}

              <button
                className="ms-page-btn ms-page-arrow"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= totalPages}
              >
                <MdChevronRight size={20} />
              </button>
            </nav>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
